import { useEffect, useRef } from 'react';

/**
 * Invokes `onTenantChange` whenever the active tenant id changes — never on the
 * initial render.
 *
 * Pages keep tenant-scoped view state locally (an open detail overlay, a cached
 * per-scenario list, a dialog holding one tenant's record). Switching tenants
 * only refetches the data a page explicitly keys on `selectedTenant`, so any
 * other local state keeps rendering the previous tenant's content under the new
 * tenant. Pass a callback that drops that state.
 *
 * `tenantId` may be `null`/`undefined` while the tenant is still resolving; the
 * transition out of that initial value is treated like any other change, which
 * is harmless because nothing tenant-scoped can be open yet.
 */
export function useResetOnTenantChange(
  tenantId: string | null | undefined,
  onTenantChange: () => void
): void {
  const previousTenantIdRef = useRef<string | null | undefined>(tenantId);

  useEffect(() => {
    if (previousTenantIdRef.current === tenantId) return;
    previousTenantIdRef.current = tenantId;
    onTenantChange();
  }, [tenantId, onTenantChange]);
}

export default useResetOnTenantChange;
